import { useEffect, useRef } from "react";

import { Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DateTime } from "luxon";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useShipmentRoute, useSupplierShipmentETAs } from "hooks";
import { ISOStringToLocalReadableTimestamp } from "utils";

import {
  buildShipmentStatusListFromRoutePointsETAs,
  buildSupplierShipmentStatusListFromShipmentLifecycle,
} from "./utils";

import type { ShipmentStatus } from "./types";

interface Props {
  shipment: SupplierShipment;
}

interface RowProps {
  status: ShipmentStatus;
}

function ShipmentStatusRow(props: RowProps) {
  const { status } = props;
  const { type, createdAt, createdBy, notes, routePointNumber, timestamp } =
    status;
  return (
    <TableRow>
      <TableCell>{type}</TableCell>
      <TableCell>
        {createdAt ? ISOStringToLocalReadableTimestamp(createdAt) : "-"}
      </TableCell>
      <TableCell>
        {timestamp ? ISOStringToLocalReadableTimestamp(timestamp) : "NA"}
      </TableCell>
      <TableCell>{notes}</TableCell>
      <TableCell>{routePointNumber ?? ""}</TableCell>
      <TableCell>{createdBy}</TableCell>
    </TableRow>
  );
}

export function SupplierShipmentStatusHistoryTable(props: Props) {
  const { shipment } = props;
  const {
    etas,
    loading: etasLoading,
    error: etaFetchError,
    refetchETAs,
  } = useSupplierShipmentETAs({ shipmentId: shipment.info.id });
  const {
    route,
    loading: routeLoading,
    error: routeFetchError,
    refetchRoute,
  } = useShipmentRoute(shipment.info.route);

  const shipmentRef = useRef<undefined | SupplierShipment>();

  useEffect(() => {
    if (shipment && shipmentRef.current?.version !== shipment.version) {
      shipmentRef.current = shipment;
      refetchETAs();
      refetchRoute();
    }
  }, [shipment, refetchETAs, refetchRoute]);

  const shipmentStatusHistory =
    buildSupplierShipmentStatusListFromShipmentLifecycle(shipment, route);
  const shipmentStatusHistoryETAs = buildShipmentStatusListFromRoutePointsETAs(
    etas ?? [],
  );

  const combinedStatus = shipmentStatusHistory.concat(
    shipmentStatusHistoryETAs,
  );
  combinedStatus.sort((a, b) => {
    const dateA = DateTime.fromISO(a.createdAt).toMillis();
    const dateB = DateTime.fromISO(b.createdAt).toMillis();
    return dateB - dateA;
  });

  if (shipment.info.booked_at && shipment.supplier) {
    const bookedQuoteStatus: ShipmentStatus = {
      type: "Shipment Booked",
      createdAt: shipment.info.booked_at,
      createdBy: `${shipment.supplier.user.first_name ?? "-"} ${shipment.supplier.user.last_name ?? "-"}`,
      notes: "",
      routePointNumber: null,
      timestamp: shipment.info.booked_at || null,
    };
    combinedStatus.push(bookedQuoteStatus);
  }

  return (
    <Stack direction="column" spacing={1}>
      <LoadingWrappedNode loading={etasLoading || routeLoading}>
        <Stack mt="1rem">
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Provided Timestamp</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Route Point</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedStatus.map((status) => (
                  <ShipmentStatusRow key={status.createdAt} status={status} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </LoadingWrappedNode>
      <ErrorTypography error={etaFetchError} />
      <ErrorTypography error={routeFetchError} />
    </Stack>
  );
}
